import React, { useContext, useMemo } from 'react';
import { Platform, View } from 'react-native';
import { createDrawerNavigator } from '@react-navigation/drawer';
import { createStackNavigator } from '@react-navigation/stack';

import { useUserContext } from '../../common/utils/context';
import { useTheme } from '../theme';
import { FieldTabBar, NavigationMenu } from '../components/navigation';
import MainTabBar from '../components/navigation/MainTabBar';
import useVisibleTabs from '../components/navigation/useVisibleTabs';
import StackHeader from '../components/navigation/StackHeader';
import { HomeRoute } from '../components/auth/AuthCheck';
import { TabPagesContext } from '../design/tabPages';
import { isLegacyMobileUi } from '../utils/legacyMobileUi';
import UserProvider from './UserProvider';
import PushNotificationProvider from '../components/other/PushNotificationProvider/PushNotificationProvider';

// Import original screens - these expect UserContext to be available
import {
	ServiceRunDetailView,
	ServiceRunCreatorView,
	ServiceRunEditorView,
	ServiceRunListView,
} from '../scenes/servicerun';

import { CoinMechDetail } from '../scenes/coinMechDetail';
import { CorrectMarketRestockView, DocketDetailView } from '../scenes/docket';
import { TicketListDetailView } from '../scenes/ticketList';
import {
	PickListDetailView,
	PickListSummaryView,
} from '../scenes/picklist';
import { SettingsView } from '../scenes/settings';
import {
	MachineDetailView,
	MachineNotesView,
	MachineDetailTabView,
	MachineDirectionsView,
	PointOfSaleListView,
	PointOfSalePlanogramView,
} from '../scenes/machine';
import { LocationNotesView } from '../scenes/location';
import { TicketCreatorView } from '../scenes/ticket';
import { MediaListView, MachinePhotoHistoryView } from '../scenes/media';
import { ConfirmMachineValuesView } from '../scenes/confirmMachineValues';
import { BarcodeScannerView } from '../scenes/barcodeScanner';
import TicketListView from '../scenes/ticket/TicketListView';
import { ConfirmStockLevelsView } from '../scenes/confirmStockLevels';
import TicketSummaryView from '../scenes/ticketList/TicketSummaryView';
import LocationDetailView from '../scenes/location/LocationDetailView';
import { StocktakeCreatorView, StocktakeDetailView, StocktakeListView } from '../scenes/stocktake';
import { StockDeliveryListView, StockDeliveryDetailView } from '../scenes/stockDelivery';
import { PendingStockListView } from '../scenes/pendingStock';
import { PurchaseOrderDetailView } from '../scenes/purchaseOrder';
import {
	SetupsTabView,
	MachineSetupDetailView as MachineSetupDetailScreen,
	MachineSetupCreatorView as MachineSetupCreatorScreen,
	MarketSetupDetailView as MarketSetupDetailScreen,
	MarketSetupCreatorView as MarketSetupCreatorScreen,
} from '../scenes/setup';
import { LocationSetupDetailView, LocationSetupCreatorView } from '../scenes/setup/location';
import { ProfileView } from '../scenes/profile';
import { DriverRouteMapView, MyDayView } from '../scenes/myDay';
import { ClockPassView } from '../scenes/clockPass';
import {
	MyLeaveRequestView,
	MyLeaveView,
	MyOpenShiftsView,
	MyRosterView,
	MyTimesheetsView,
} from '../scenes/roster';
import {
	MyAccountView,
	MyDocumentsView,
	MyDocumentViewerView,
	MyEmploymentView,
	MyProfileAboutYouView,
	MyWarningDetailView,
	MyWarningsView,
	ReportIncidentView,
} from '../scenes/myAccount';
import { ProcedureDetailView, ProcedureListView } from '../scenes/procedures';
import { ChecklistApprovalsView, ChecklistFormView, ChecklistSignOffView } from '../scenes/checklist';
import { TrainingDetailView, TrainingListView } from '../scenes/training';
import { WarehouseView } from '../scenes/warehouse';
import { LeadCreatorView, LeadDetailView, LeadListView } from '../scenes/lead';
import DesignGalleryView from '../scenes/designGallery/DesignGalleryView';
import DriverDayProvider from '../scenes/driver/DriverDayProvider';
import { APP_TABS, DRIVER_ROUTE } from '../scenes/driver/driverRoutes';
import AllRunsView from '../scenes/driver/AllRunsView';
import MyRunOverviewView from '../scenes/driver/MyRunOverviewView';
import EmployeeDayPreviewView from '../scenes/driver/EmployeeDayPreviewView';
import EndOfDayView from '../scenes/driver/EndOfDayView';
import { END_OF_DAY_ROUTE } from '../scenes/driver/endOfDay/endOfDayRoutes';
import MoneyBagsView from '../scenes/driver/endOfDay/MoneyBagsView';
import ReturnKeysView from '../scenes/driver/endOfDay/ReturnKeysView';
import ReturnStockView from '../scenes/driver/endOfDay/ReturnStockView';
import KeysAccessView from '../scenes/driver/KeysAccessView';
import PickingMyDayView from '../scenes/driver/PickingMyDayView';
import RecalcPickView from '../scenes/driver/RecalcPickView';
import ProductChangesView from '../scenes/driver/ProductChangesView';
import ReorderStopsView from '../scenes/driver/ReorderStopsView';
import RunListView from '../scenes/driver/RunListView';
import RunOverviewView from '../scenes/driver/RunOverviewView';
import StartOfDayView from '../scenes/driver/StartOfDayView';
import StopMachineView from '../scenes/driver/StopMachineView';
import StopView from '../scenes/driver/StopView';

import ChecklistNotificationsView from '../scenes/notifications/ChecklistNotificationsView';

const Drawer = createDrawerNavigator();
const Stack = createStackNavigator();

// Redesigned screens draw their own header (design Screen).
const SCREEN_WITHOUT_STACK_HEADER = { headerShown: false };
// Bottom bar pages swap in place like tabs instead of sliding in (no swipe-back either).
const TAB_PAGE_OPTIONS = { headerShown: false, animation: 'none' };

// Main stack navigator with all authenticated screens
const MainStackNavigator = () => {
	const { theme } = useTheme();
	const { colors } = theme.ui;
	const { user } = useUserContext();
	const isLegacyUi = isLegacyMobileUi(user);
	const tabPages = useContext(TabPagesContext);
	// Only a bar page opened from Home or the bar swaps in place; elsewhere (a run opened
	// from All Runs, All Runs from a driver's side menu) it slides in and keeps swipe-back.
	const tabPageOptions = ({ route, navigation }) => (
		tabPages.has(route.name) && navigation.getState().routes.findIndex(item => item.key === route.key) <= 1
			? TAB_PAGE_OPTIONS
			: SCREEN_WITHOUT_STACK_HEADER
	);

	return (
		<View style={{ flex: 1 }}>
			<Stack.Navigator
				screenOptions={() => ({
					// Same "Back" label as the redesign Screen header on every platform.
					headerBackTitle: 'Back',
					headerBackButtonDisplayMode: 'default',
					// Same look as the redesign Screen header for screens not restyled yet.
					headerStyle: {
						backgroundColor: colors.surface,
						borderBottomWidth: 0,
						shadowOpacity: 0,
						elevation: 0,
					},
					headerTintColor: colors.accent,
					headerTitleStyle: {
						display: 'none',
					},
					headerLeftContainerStyle: {
						paddingBottom: Platform.OS === 'web' ? 0 : 5,
					},
					headerRightContainerStyle: {
						paddingBottom: Platform.OS === 'web' ? 0 : 5,
					},
					cardStyle: {
						backgroundColor: theme.mainBg1,
						...(Platform.OS === 'web' ? { flex: 1 } : {}),
					},
					// Default header plus the menu button next to any screen action.
					header: props => <StackHeader {...props} />,
				})}
			>
				<Stack.Screen
					name="Home"
					component={HomeRoute}
					options={{
						title: '',
						// Home is the redesigned HomeView unless the user is on the legacy screens.
						headerShown: isLegacyUi,
						headerTitleStyle: {
							...theme.ui.type.cardTitle,
							color: colors.text,
						},
					}}
				/>
				<Stack.Screen name={DRIVER_ROUTE.START_OF_DAY} component={StartOfDayView} options={tabPageOptions} />
				<Stack.Screen name={DRIVER_ROUTE.RUN_OVERVIEW} component={RunOverviewView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name={DRIVER_ROUTE.KEYS_ACCESS} component={KeysAccessView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name={DRIVER_ROUTE.RECALC_PICK} component={RecalcPickView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name={DRIVER_ROUTE.PRODUCT_CHANGES} component={ProductChangesView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name={DRIVER_ROUTE.RUN_LIST} component={RunListView} options={tabPageOptions} />
				<Stack.Screen name={DRIVER_ROUTE.STOP} component={StopView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name={DRIVER_ROUTE.STOP_MACHINE} component={StopMachineView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name={DRIVER_ROUTE.REORDER_STOPS} component={ReorderStopsView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name={DRIVER_ROUTE.END_OF_DAY} component={EndOfDayView} options={tabPageOptions} />
				<Stack.Screen name={END_OF_DAY_ROUTE.MONEY_BAGS} component={MoneyBagsView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name={END_OF_DAY_ROUTE.RETURN_KEYS} component={ReturnKeysView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name={END_OF_DAY_ROUTE.RETURN_STOCK} component={ReturnStockView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name={DRIVER_ROUTE.PICKING_MY_DAY} component={PickingMyDayView} options={tabPageOptions} />
				<Stack.Screen name={DRIVER_ROUTE.ALL_RUNS} component={AllRunsView} options={tabPageOptions} />
				<Stack.Screen name={DRIVER_ROUTE.MY_RUN_OVERVIEW} component={MyRunOverviewView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name={DRIVER_ROUTE.EMPLOYEE_DAY_PREVIEW} component={EmployeeDayPreviewView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name="MyDayView" component={MyDayView} />
				<Stack.Screen name="DriverRouteMapView" component={DriverRouteMapView} options={SCREEN_WITHOUT_STACK_HEADER} />
				{/* My day users have the board as Home; the classic run list needs its own screen. */}
				<Stack.Screen name="ServiceRunListView" component={ServiceRunListView} />
				<Stack.Screen name="ServiceRunDetailView" component={ServiceRunDetailView} options={{ headerShown: false }} />
				<Stack.Screen name="DocketDetailView" component={DocketDetailView} />
				<Stack.Screen name="CorrectMarketRestockView" component={CorrectMarketRestockView} options={{ headerShown: false }} />
				<Stack.Screen name="CoinMechDetail" component={CoinMechDetail} options={{ headerShown: false }} />
				<Stack.Screen name="PickListDetailView" component={PickListDetailView} options={{ headerShown: false }} />
				<Stack.Screen name="PickListSummaryView" component={PickListSummaryView} options={{ headerShown: false }} />
				<Stack.Screen name="ConfirmStockLevelsView" component={ConfirmStockLevelsView} options={{ headerShown: false }} />
				<Stack.Screen name="TicketListDetailView" component={TicketListDetailView} options={{ headerShown: false }} />
				<Stack.Screen name="SettingsView" component={SettingsView} options={{ headerShown: false }} />
				<Stack.Screen name="ProfileView" component={ProfileView} options={{ headerShown: false }} />
				<Stack.Screen name="ClockPassView" component={ClockPassView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name="MyAccountView" component={MyAccountView} options={{ headerShown: false }} />
				<Stack.Screen name="MyEmploymentView" component={MyEmploymentView} options={{ headerShown: false }} />
				<Stack.Screen name="MyWarningsView" component={MyWarningsView} options={{ headerShown: false }} />
				<Stack.Screen name="MyWarningDetailView" component={MyWarningDetailView} options={{ headerShown: false }} />
				<Stack.Screen name="MyDocumentsView" component={MyDocumentsView} options={{ headerShown: false }} />
				<Stack.Screen name="MyDocumentViewerView" component={MyDocumentViewerView} options={{ headerShown: false }} />
				<Stack.Screen name="ReportIncidentView" component={ReportIncidentView} options={{ headerShown: false }} />
				<Stack.Screen name="MyRosterView" component={MyRosterView} options={{ headerShown: false }} />
				<Stack.Screen name="MyOpenShiftsView" component={MyOpenShiftsView} options={{ headerShown: false }} />
				<Stack.Screen name="MyTimesheetsView" component={MyTimesheetsView} options={{ headerShown: false }} />
				<Stack.Screen name="MyLeaveView" component={MyLeaveView} options={{ headerShown: false }} />
				<Stack.Screen name="MyLeaveRequestView" component={MyLeaveRequestView} options={{ headerShown: false }} />
				<Stack.Screen name="ProcedureListView" component={ProcedureListView} options={{ headerShown: false }} />
				<Stack.Screen name="ProcedureDetailView" component={ProcedureDetailView} options={{ headerShown: false }} />
				<Stack.Screen name="ChecklistFormView" component={ChecklistFormView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name="TrainingListView" component={TrainingListView} options={{ headerShown: false }} />
				<Stack.Screen name="TrainingDetailView" component={TrainingDetailView} options={{ headerShown: false }} />
				<Stack.Screen name="LeadListView" component={LeadListView} options={{ headerShown: false }} />
				<Stack.Screen name="LeadDetailView" component={LeadDetailView} options={{ headerShown: false }} />
				<Stack.Screen name="LeadCreatorView" component={LeadCreatorView} options={{ headerShown: false }} />
				<Stack.Screen name="WarehouseView" component={WarehouseView} options={{ headerShown: false }} />
				<Stack.Screen name="MyProfileAboutYouView" component={MyProfileAboutYouView} options={{ headerShown: false }} />
				<Stack.Screen name="ChecklistSignOffView" component={ChecklistSignOffView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name="ChecklistApprovalsView" component={ChecklistApprovalsView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name="ChecklistNotificationsView" component={ChecklistNotificationsView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name="MachineDetail" component={MachineDetailView} />
				<Stack.Screen name="MachineDetailTabView" component={MachineDetailTabView} options={{ headerShown: false }} />
				<Stack.Screen name="MachineDirectionsView" component={MachineDirectionsView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name="PointOfSaleListView" component={PointOfSaleListView} options={{ headerShown: false }} />
				<Stack.Screen name="PointOfSalePlanogramView" component={PointOfSalePlanogramView} />
				<Stack.Screen name="MachineNotes" component={MachineNotesView} options={{ headerShown: false }} />
				<Stack.Screen name="LocationDetailView" component={LocationDetailView} />
				<Stack.Screen name="LocationNotes" component={LocationNotesView} options={{ headerShown: false }} />
				<Stack.Screen name="TicketCreator" component={TicketCreatorView} options={{ headerShown: false }} />
				<Stack.Screen name="TicketListView" component={TicketListView} options={{ headerShown: false }} />
				<Stack.Screen name="MediaListView" component={MediaListView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name="MachinePhotoHistoryView" component={MachinePhotoHistoryView} options={{ headerShown: false }} />
				<Stack.Screen name="ConfirmMachineValuesView" component={ConfirmMachineValuesView} options={{ headerShown: false }} />
				<Stack.Screen name="ServiceRunCreatorView" component={ServiceRunCreatorView} options={{ headerShown: false }} />
				<Stack.Screen name="ServiceRunEditorView" component={ServiceRunEditorView} options={{ headerShown: false }} />
				<Stack.Screen name="BarcodeScannerView" component={BarcodeScannerView} options={{ headerShown: false }} />
				<Stack.Screen name="StocktakeListView" component={StocktakeListView} options={{ headerShown: false }} />
				<Stack.Screen name="StocktakeDetailView" component={StocktakeDetailView} options={{ headerShown: false }} />
				<Stack.Screen name="StocktakeCreatorView" component={StocktakeCreatorView} options={{ headerShown: false }} />
				<Stack.Screen name="StockDeliveryListView" component={StockDeliveryListView} options={{ headerShown: false }} />
				<Stack.Screen name="StockDeliveryDetailView" component={StockDeliveryDetailView} options={{ headerShown: false }} />
				<Stack.Screen name="PendingStockListView" component={PendingStockListView} options={{ headerShown: false }} />
				<Stack.Screen name="PurchaseOrderDetailView" component={PurchaseOrderDetailView} options={{ headerShown: false }} />
				<Stack.Screen name="TicketSummaryView" component={TicketSummaryView} options={SCREEN_WITHOUT_STACK_HEADER} />
				<Stack.Screen name="SetupsView" component={SetupsTabView} options={{ headerShown: false }} />
				<Stack.Screen name="MachineSetupDetailView" component={MachineSetupDetailScreen} options={{ headerShown: false }} />
				<Stack.Screen name="MachineSetupCreatorView" component={MachineSetupCreatorScreen} options={{ headerShown: false }} />
				<Stack.Screen name="MarketSetupDetailView" component={MarketSetupDetailScreen} options={{ headerShown: false }} />
				<Stack.Screen name="MarketSetupCreatorView" component={MarketSetupCreatorScreen} options={{ headerShown: false }} />
				<Stack.Screen name="LocationSetupDetailView" component={LocationSetupDetailView} options={{ headerShown: false }} />
				<Stack.Screen name="LocationSetupCreatorView" component={LocationSetupCreatorView} options={{ headerShown: false }} />
				{
					// Dev builds only: redesign component gallery, opened from Settings.
					// eslint-disable-next-line no-undef
					__DEV__ && (
						<Stack.Screen name="DesignGalleryView" component={DesignGalleryView} options={{ headerShown: false }} />
					)
				}
			</Stack.Navigator>
			{isLegacyUi ? <FieldTabBar /> : <MainTabBar />}
		</View>
	);
};

// Tells Screen which routes are bottom bar pages (needs the shell provider above it).
const TabPagesProvider = ({ children }) => {
	const visibleTabs = useVisibleTabs();
	const tabPages = useMemo(() => (
		new Set(visibleTabs.length > 1 ? visibleTabs.map(tab => APP_TABS[tab].route) : [])
	), [visibleTabs]);

	return <TabPagesContext.Provider value={tabPages}>{children}</TabPagesContext.Provider>;
};

// The shell provider sits inside the drawer's screen, around the stack and its bottom bar.
const MainStackScreen = () => (
	<DriverDayProvider>
		<TabPagesProvider>
			<MainStackNavigator />
		</TabPagesProvider>
	</DriverDayProvider>
);

// Drawer navigator wrapping the main stack
const DrawerNavigator = () => {
	const { theme: { ui: { colors } } } = useTheme();

	return (
		<Drawer.Navigator
			screenOptions={{
				headerShown: false,
				overlayColor: colors.overlay,
				drawerStyle: {
					backgroundColor: colors.surface,
					...(Platform.OS === 'web' ? { height: '100%' } : {}),
				},
				...(Platform.OS === 'web' ? {
					sceneContainerStyle: { flex: 1 },
				} : {}),
			}}
			drawerContent={(props) => (
				<NavigationMenu {...props} />
			)}
		>
			<Drawer.Screen
				name="MainStack"
				component={MainStackScreen}
			/>
		</Drawer.Navigator>
	);
};

// Wrap the entire authenticated app with UserProvider
const AuthenticatedAppStack = () => {
	return (
		<UserProvider>
			<PushNotificationProvider>
				<DrawerNavigator />
			</PushNotificationProvider>
		</UserProvider>
	);
};

export default AuthenticatedAppStack;
